import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import SaveIcon from "@mui/icons-material/Save";
import request from "../../../Helpers/API";
import PlantillaScreen from "../../helpers/PlantillaScreen";
import { LoaderScreen } from "../../components/LoaderScreen";

const EXTENSIONES_VALIDAS = ["jpg", "jpeg", "pdf"];
const COMENTARIO_DEFAULT = "Carga documentacion desde expediente digital";

const FORM_VACIO = { archivo: "", nombreArchivo: "", extension: "", vigencia: "", comentarios: "" };

/**
 * CRM_ModalActualizarDocumento
 * Modal para cargar/reemplazar el archivo de un documento del Expediente Digital.
 *
 * Props:
 *   open        — bool, controla si se muestra el modal
 *   onClose     — fn() al cancelar/cerrar
 *   documento   — item de la fila (iddocumento, idclientedocumento, orden, documento -nombre-)
 *   idcliente   — cliente dueño del expediente
 *   onGuardado  — fn() se llama tras guardar con éxito, para refrescar el listado
 */
export const CRM_ModalActualizarDocumento = ({ open, onClose, documento, idcliente, onGuardado }) => {
  const [form, setForm] = useState(FORM_VACIO);
  const [arrastrando, setArrastrando] = useState(false);
  const [loader, setLoader] = useState({ mostrar: false, alerta: false, error: false, mensaje: "" });
  const inputArchivoRef = useRef(null);

  useEffect(() => {
    if (open) setForm(FORM_VACIO);
  }, [open, documento]);

  if (!open) return null;

  // Valida extensión, la extrae del nombre y convierte el archivo a base64 (con el mime-type integrado)
  const procesarArchivo = (file) => {
    if (!file) return;
    const extension = file.name.split(".").pop().toLowerCase();
    if (!EXTENSIONES_VALIDAS.includes(extension)) {
      Swal.fire("EXPEDIENTE", "Archivo con formato no válido (solo jpg, jpeg o pdf)", "error");
      return;
    }
    const lector = new FileReader();
    lector.onloadend = () => {
      setForm((prev) => ({
        ...prev,
        archivo: lector.result,
        nombreArchivo: file.name,
        extension,
      }));
    };
    lector.readAsDataURL(file);
  };

  const onSeleccionarArchivo = ({ target }) => procesarArchivo(target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    procesarArchivo(e.dataTransfer.files?.[0]);
  };

  const quitarArchivo = () => setForm((prev) => ({ ...prev, archivo: "", nombreArchivo: "", extension: "" }));

  const handleChange = (campo) => (e) => setForm((prev) => ({ ...prev, [campo]: e.target.value }));

  const validar = () => {
    if (!form.archivo) {
      Swal.fire("EXPEDIENTE", "Selecciona un archivo para continuar", "warning");
      return false;
    }
    if (!form.vigencia) {
      Swal.fire("EXPEDIENTE", "La vigencia es obligatoria", "warning");
      return false;
    }
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaVigencia = new Date(`${form.vigencia}T00:00:00`);
    if (fechaVigencia < hoy) {
      Swal.fire("EXPEDIENTE", "La vigencia no puede ser anterior a hoy", "warning");
      return false;
    }
    return true;
  };

  const onGuardar = () => {
    if (!validar()) return;

    // Documento nuevo (nunca cargado para este cliente): idclientedocumento y orden se mandan en 0
    const documentoExistente = documento?.idclientedocumento > 0;
    const payload = {
      idclientedocumento: documentoExistente ? Number(documento.idclientedocumento) : 0,
      orden: documentoExistente ? Number(documento?.orden ?? 0) : 0,
      iddocumento: Number(documento?.iddocumento),
      idcliente: Number(idcliente),
      documento: form.archivo,
      extension: form.extension,
      vigencia: form.vigencia,
      estatus: 1,
      comentarios: form.comentarios.trim() !== "" ? form.comentarios.trim() : COMENTARIO_DEFAULT,
    };

    setLoader({ ...loader, mostrar: true });

    request
      .post("ClienteDocumentacion", payload)
      .then(async () => {
    setLoader({ ...loader, mostrar: false });
        await Swal.fire("EXPEDIENTE", "Documento cargado con éxito", "success");
        onGuardado?.();
        onClose?.();
      })
      .catch((error) => {
        setLoader({ ...loader, mostrar: false });
        Swal.fire("EXPEDIENTE", JSON.stringify(error), "error");
      });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div className="ram-backdrop" onClick={handleBackdropClick}>
      <PlantillaScreen
        titulo={`Actualizar documento${documento?.documento ? ` · ${documento.documento}` : ""}`}
        className={"ram-modal crm-modal-actualizar-documento"}
      >
        <button className="ram-close" onClick={onClose} type="button">
          <CloseIcon style={{ fontSize: 14 }} />
        </button>

        <LoaderScreen loader={loader} />

        <div className="ram-body">
          <div className="crm-mad-dropzone-field">
            <label className="pa-label--sm">Archivo (.jpg, .jpeg, .pdf)</label>
            <input
              ref={inputArchivoRef}
              type="file"
              accept=".jpg,.jpeg,.pdf"
              hidden
              onChange={onSeleccionarArchivo}
            />
            {form.nombreArchivo ? (
              <div className="crm-mad-archivo-chip">
                <DescriptionOutlinedIcon />
                <span>{form.nombreArchivo}</span>
                <button type="button" onClick={quitarArchivo} aria-label="Quitar archivo">
                  <CloseIcon style={{ fontSize: 14 }} />
                </button>
              </div>
            ) : (
              <div
                className={`crm-mad-dropzone${arrastrando ? " arrastrando" : ""}`}
                onClick={() => inputArchivoRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={onDrop}
              >
                <CloudUploadOutlinedIcon />
                <p>Arrastra tu archivo aquí o haz clic para buscar</p>
                <span>PDF, JPG o JPEG, máximo 10 MB</span>
              </div>
            )}
          </div>

          <div className="ram-field">
            <div className="ram-field__icon">
              <CalendarMonthIcon />
            </div>
            <div className="ram-field__content">
              <label className="pa-label--sm">Vigencia</label>
              <div className="pa-input-wrap">
                <input
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={form.vigencia}
                  onChange={handleChange("vigencia")}
                />
              </div>
            </div>
          </div>

          <div className="ram-field">
            <div className="ram-field__icon">
              <StickyNote2OutlinedIcon />
            </div>
            <div className="ram-field__content">
              <label className="pa-label--sm">Comentario (opcional)</label>
              <textarea
                className="crm-mad-textarea"
                rows={3}
                placeholder={COMENTARIO_DEFAULT}
                value={form.comentarios}
                onChange={handleChange("comentarios")}
              />
            </div>
          </div>
        </div>

        <div className="ram-footer">
          <button type="button" className="ram-btn ram-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="ram-btn ram-btn--primary" onClick={onGuardar} disabled={loader.mostrar}>
            {loader.mostrar ? <span className="ram-btn__spinner" /> : <SaveIcon />}
            {loader.mostrar ? "Guardando..." : "Guardar documento"}
          </button>
        </div>
      </PlantillaScreen>
    </div>
  );
};
